import { translationKeys, type TranslationKey } from "../keys.js";

export const defaultLocale = "en-US" as const;

export type LocaleCode = typeof defaultLocale;

export type LocaleDictionary = Record<TranslationKey, string>;

export const enUSLocaleDictionary = {
  [translationKeys.app.name]: "AmpleCare",
  [translationKeys.app.loading]: "Loading",
  [translationKeys.app.error]: "Something went wrong",
  [translationKeys.auth.signIn]: "Sign in",
  [translationKeys.auth.signOut]: "Sign out",
  [translationKeys.auth.continue]: "Continue",
  [translationKeys.navigation.dashboard]: "Dashboard",
  [translationKeys.navigation.home]: "Home",
  [translationKeys.navigation.clinicianWorkspace]: "Clinician workspace",
  [translationKeys.navigation.patients]: "Patients",
  [translationKeys.navigation.inbox]: "Inbox",
  [translationKeys.navigation.tasks]: "Tasks",
  [translationKeys.navigation.documents]: "Documents",
  [translationKeys.navigation.profile]: "Profile",
  [translationKeys.navigation.settings]: "Settings",
  [translationKeys.patient.timeline]: "Timeline",
  [translationKeys.patient.upcomingVisits]: "Upcoming visits",
  [translationKeys.patient.medications]: "Medications",
  [translationKeys.patient.documents]: "Documents",
  [translationKeys.shell.badge]: "Clinician workspace",
  [translationKeys.shell.title]: "Clinician workspace",
  [translationKeys.shell.subtitle]:
    "Patient communication, tasks, documents, and care details in one workspace.",
  [translationKeys.shell.statePatient]: "Selected patient",
  [translationKeys.shell.stateScope]: "List scope",
  [translationKeys.shell.stateDetailTab]: "Detail tab",
  [translationKeys.shell.workspacePatientsGroup]: "Patients",
  [translationKeys.shell.workspaceEngagementGroup]: "Engagement",
  [translationKeys.shell.workspaceAiGroup]: "AI review",
  [translationKeys.shell.workspaceMetricPreOp]: "Pre-op",
  [translationKeys.shell.workspaceMetricPostOp]: "Post-op",
  [translationKeys.shell.workspaceMetricImagingActive]: "Imaging active",
  [translationKeys.shell.workspaceMetric60Minutes]: "Needs reply in 60 minutes",
  [translationKeys.shell.workspaceMetric24Hours]: "Touched in 24 hours",
  [translationKeys.shell.workspaceMetricAiApproved]: "Auto-approved drafts",
  [translationKeys.shell.workspaceMetricAiEdited]: "Drafts edited by staff",
  [translationKeys.shell.metricPendingApprovals]: "Pending approvals",
  [translationKeys.shell.metricEscalations]: "Escalations",
  [translationKeys.shell.workspaceListTitle]: "Patient inbox",
  [translationKeys.shell.workspaceListSubtitle]:
    "Review your patient list, attention filters, and open conversations.",
  [translationKeys.shell.workspaceListMyPatients]: "My patients",
  [translationKeys.shell.workspaceListMyPatientsSupport]:
    "Your assigned patients and current care-team priorities.",
  [translationKeys.shell.workspaceListAllPatients]: "All patients",
  [translationKeys.shell.workspaceListAllPatientsSupport]:
    "All patients available in this workspace.",
  [translationKeys.shell.workspaceListAttentionOnly]: "Patients requiring attention only",
  [translationKeys.shell.workspaceListAttentionSupport]:
    "Patients with unread messages or pending documents.",
  [translationKeys.shell.workspaceListSearchPlaceholder]: "Search patients or program",
  [translationKeys.shell.workspaceListVisiblePatients]: "Visible patients",
  [translationKeys.shell.workspaceListNoPatients]:
    "No patients match the current My or All scope, attention filter, and search query.",
  [translationKeys.shell.workspaceSelectedPatient]: "Selected patient",
  [translationKeys.shell.workspacePatientIdentifier]: "Identifier",
  [translationKeys.shell.workspaceAssignedToMyPatients]: "Assigned to My Patients",
  [translationKeys.shell.workspaceAddToMyPatients]: "Add to My Patients",
  [translationKeys.shell.workforceSessionLoadingTitle]:
    "Loading workspace access",
  [translationKeys.shell.workforceSessionLoadingBody]:
    "Loading your workspace access.",
  [translationKeys.shell.workforceSessionSelectionTitle]:
    "Choose a workspace profile",
  [translationKeys.shell.workforceSessionSelectionBody]:
    "Pick the profile you want to use for this session.",
  [translationKeys.shell.workforceSessionErrorBody]:
    "The workspace could not open. Refresh the page or open it again in a new tab.",
  [translationKeys.shell.workspaceNoPatientSelectedTitle]: "No patient selected",
  [translationKeys.shell.workspaceNoPatientSelectedBody]:
    "Choose a patient from the inbox to restore conversation, task, document, and detail context.",
  [translationKeys.shell.workspaceChatTitle]: "Conversation",
  [translationKeys.shell.workspaceChatSubtitle]:
    "Patient messages and care-team follow-up.",
  [translationKeys.shell.workspaceChatReviewPendingApproval]: "Pending approval",
  [translationKeys.shell.workspaceChatReviewApproved]: "Approved",
  [translationKeys.shell.workspaceChatReviewApprovedEdited]: "Approved with edits",
  [translationKeys.shell.workspaceChatReviewRejected]: "Rejected",
  [translationKeys.shell.workspaceChatConfidence]: "Confidence",
  [translationKeys.shell.workspaceChatCriticality]: "Criticality",
  [translationKeys.shell.workspaceChatClinicianNotes]: "Clinician notes",
  [translationKeys.shell.workspaceChatReviewActionNotice]:
    "Use the clinician controls below to complete message review.",
  [translationKeys.shell.workspaceChatDeliverySent]: "Sent",
  [translationKeys.shell.workspaceChatDeliveryDelivered]: "Delivered",
  [translationKeys.shell.workspaceChatSubmittingBody]:
    "The clinician action is being applied and the conversation is refreshing.",
  [translationKeys.shell.workspaceActionApprove]: "Approve",
  [translationKeys.shell.workspaceActionReject]: "Reject",
  [translationKeys.shell.workspaceActionEdit]: "Edit",
  [translationKeys.shell.workspaceActionSave]: "Save",
  [translationKeys.shell.workspaceActionSendReply]: "Send reply",
  [translationKeys.shell.workspaceActionCompleteTask]: "Complete task",
  [translationKeys.shell.workspaceActionMarkDocumentReviewed]: "Mark document reviewed",
  [translationKeys.shell.workspaceActionRefreshSummary]: "Refresh summary",
  [translationKeys.shell.workspaceActionClearAttention]: "Clear attention",
  [translationKeys.shell.workspaceActionSubmitting]: "Submitting...",
  [translationKeys.shell.workspaceActionAddToMyPatientsSuccess]:
    "Patient was added to My Patients.",
  [translationKeys.shell.workspaceActionReplySuccess]:
    "Reply was saved to the patient conversation.",
  [translationKeys.shell.workspaceActionApproveSuccess]:
    "Message review was approved.",
  [translationKeys.shell.workspaceActionRejectSuccess]:
    "Message review was rejected.",
  [translationKeys.shell.workspaceActionCompleteTaskSuccess]:
    "Task assignment was completed.",
  [translationKeys.shell.workspaceActionMarkDocumentReviewedSuccess]:
    "Document was marked reviewed.",
  [translationKeys.shell.workspaceActionRefreshSummarySuccess]:
    "Case summary was refreshed.",
  [translationKeys.shell.workspaceActionPatientRecordSuccess]:
    "Patient details were saved.",
  [translationKeys.shell.workspaceActionClearAttentionSuccess]:
    "Patient attention was acknowledged.",
  [translationKeys.shell.workspaceActionError]:
    "The clinician action could not be saved.",
  [translationKeys.shell.workspaceComposerInputPlaceholder]:
    "Type a clinician reply...",
  [translationKeys.shell.workspaceComposerActionNotice]:
    "Replies and review actions are saved.",
  [translationKeys.shell.workspaceComposerScaffoldNotice]:
    "Reply, approve, and reject actions are saved.",
  [translationKeys.shell.workspaceComposerPlaceholder]:
    "Type a clinician reply...",
  [translationKeys.shell.workspaceDetailsTitle]: "Patient details",
  [translationKeys.shell.workspacePanelTitle]: "Focus",
  [translationKeys.shell.workspacePanelSubtitle]:
    "Inbox, tasks, and documents for the selected patient.",
  [translationKeys.shell.workspacePanelInboxSubtitle]:
    "Unread messages and pending documents.",
  [translationKeys.shell.workspacePanelTasksSubtitle]:
    "Tasks, approvals, and delivery follow-up.",
  [translationKeys.shell.workspacePanelDocumentsSubtitle]:
    "Documents for the selected patient.",
  [translationKeys.shell.workspacePanelPatients]: "Patients",
  [translationKeys.shell.workspacePanelAttention]: "Attention",
  [translationKeys.shell.workspacePanelUnread]: "Unread",
  [translationKeys.shell.workspacePanelPendingDocuments]: "Pending docs",
  [translationKeys.shell.workspacePanelEmptyInbox]: "No patients match the current inbox filter.",
  [translationKeys.shell.workspacePanelEmptyTasks]:
    "No task, approval, or delivery items are visible for the selected patient.",
  [translationKeys.shell.workspacePanelEmptyDocuments]:
    "No document summaries are visible for the selected patient.",
  [translationKeys.shell.workspaceDetailInformation]: "Patient information",
  [translationKeys.shell.workspaceDetailCaseSummary]: "Case summary",
  [translationKeys.shell.workspaceDetailAppointments]: "Appointments",
  [translationKeys.shell.workspaceDetailProcedures]: "Procedures",
  [translationKeys.shell.workspaceDetailMedications]: "Medications",
  [translationKeys.shell.workspaceDetailDocuments]: "Documents",
  [translationKeys.shell.workspaceDetailTaskAssignments]: "Task assignments",
  [translationKeys.shell.workspaceDetailApprovals]: "Approvals",
  [translationKeys.shell.workspaceDetailDeliveries]: "Deliveries",
  [translationKeys.shell.workspaceDetailDocumentsCount]: "Documents",
  [translationKeys.shell.workspaceDetailRowTask]: "Task",
  [translationKeys.shell.workspaceDetailRowApproval]: "Approval",
  [translationKeys.shell.workspaceDetailRowDelivery]: "Delivery",
  [translationKeys.shell.workspaceDetailRowDocument]: "Document",
  [translationKeys.shell.workspaceInfoAssignedClinician]: "Assigned clinician",
  [translationKeys.shell.workspaceInfoPrimaryProgram]: "Primary program",
  [translationKeys.shell.workspaceInfoNextAppointment]: "Next appointment",
  [translationKeys.shell.workspaceInfoContactWindow]: "Contact window",
  [translationKeys.shell.workspaceDocumentCreatedBy]: "Created by",
  [translationKeys.shell.workspaceDocumentCreatedAt]: "Created at",
  [translationKeys.shell.workspaceDocumentDownloadReady]: "Available for review",
  [translationKeys.shell.workspaceDocumentMetadataOnly]: "Metadata only",
  [translationKeys.shell.workspaceAppointmentRow]: "Upcoming visit",
  [translationKeys.shell.workspaceProcedureRow]: "Upcoming procedure",
  [translationKeys.shell.workspaceMedicationRow]: "Medication note",
  [translationKeys.shell.workspaceDocumentRow]: "Document set",
  [translationKeys.shell.workspaceStatusNeedsReply]: "Needs reply",
  [translationKeys.shell.workspaceStatusReviewReady]: "Review ready",
  [translationKeys.shell.workspaceStatusAwaitingDocument]: "Awaiting document",
  [translationKeys.shell.workspacePatientOne]: "Merritt source patient",
  [translationKeys.shell.workspacePatientTwo]: "Merritt imaging patient",
  [translationKeys.shell.workspacePatientThree]: "Merritt admissions patient",
  [translationKeys.shell.workspaceSummaryPatientOne]:
    "Intake context, messages, and care-team follow-up are ready.",
  [translationKeys.shell.workspaceSummaryPatientTwo]:
    "Imaging context, messages, and care-team follow-up are ready.",
  [translationKeys.shell.workspaceSummaryPatientThree]:
    "Admissions context, messages, and care-team follow-up are ready.",
  [translationKeys.shell.workspaceMessagePatientSender]: "Patient",
  [translationKeys.shell.workspaceMessageClinicianSender]: "Clinician",
  [translationKeys.shell.workspaceMessageSystemSender]: "System",
  [translationKeys.shell.workspaceMessagePatientBodyOne]:
    "I have a question about my care plan.",
  [translationKeys.shell.workspaceMessageClinicianBodyOne]:
    "Thanks for reaching out. I will review this with the care team.",
  [translationKeys.shell.workspaceMessageSystemBodyOne]:
    "Care-team update recorded.",
  [translationKeys.shell.workspaceMessagePatientBodyTwo]:
    "I need help with my imaging instructions.",
  [translationKeys.shell.workspaceMessageClinicianBodyTwo]:
    "We will confirm the next imaging step with you.",
  [translationKeys.shell.workspaceMessageSystemBodyTwo]:
    "Imaging follow-up recorded.",
  [translationKeys.shell.workspaceMessagePatientBodyThree]:
    "I have an admissions question.",
  [translationKeys.shell.workspaceMessageClinicianBodyThree]:
    "We will help you prepare for the next step.",
  [translationKeys.shell.workspaceMessageSystemBodyThree]:
    "Admissions follow-up recorded.",
  [translationKeys.shell.flagPreOp]: "Pre-op",
  [translationKeys.shell.flagPostOp]: "Post-op",
  [translationKeys.shell.flagImagingActive]: "Imaging active",
  [translationKeys.shell.flagPreAdmission]: "Pre-admission",
  [translationKeys.shell.flagChronicFollowUp]: "Chronic follow-up",
  [translationKeys.settings.language]: "Language",
  [translationKeys.settings.notifications]: "Notifications",
} satisfies LocaleDictionary;
